import { LAUNCH_RETURN_URLS_MAX } from "@sagot/constants";
import { useTranslation } from "@sagot/i18n";
import { Badge, Button, Field, Input, ListEditor, type ListEditorLabels, type ListEntryResult } from "@sagot/ui";
import { useState } from "react";
import { Gate, useCanManage } from "../panels/panel-kit";
import { parseHttpsUrl, sameList, type UrlProblem } from "./launch-settings-model";
import { useSaveLaunchSettings, type LaunchSettings } from "./use-launch-settings";

const K = "launch.urls";
const PROBLEM_KEYS: Readonly<Record<UrlProblem, string>> = {
  invalid: "invalid",
  http: "invalidHttp",
  wildcard: "invalidWildcard",
  credentials: "invalidCredentials",
  fragment: "invalidFragment",
  tooLong: "invalidLength",
};

/**
 * Secure launch "Your endpoints" card (mockup secure-launch pin 2, PID-006, PID-008): the mint URL on the operator's server
 * (used to start a bound launch and to relaunch an expired frame) and the exact return addresses a launch may send the customer
 * back to. Both are https only; a launch asking for any other return address is refused.
 */
export function LaunchUrlsCard({ settings }: { settings: LaunchSettings }) {
  const { t } = useTranslation();
  const manage = useCanManage("customers.manage_launch");
  const saver = useSaveLaunchSettings(t(`${K}.saved`));
  const [mintDraft, setMintDraft] = useState<string | null>(null);
  const [mintError, setMintError] = useState<string | null>(null);
  const [returnDraft, setReturnDraft] = useState<string[] | null>(null);
  const savedMint = settings.mint_url ?? "";
  const mint = mintDraft ?? savedMint;
  const returns = returnDraft ?? settings.return_urls;
  const mintChanged = mintDraft !== null && mintDraft.trim() !== savedMint;
  const returnsChanged = returnDraft !== null && !sameList(returnDraft, settings.return_urls);
  const dirty = mintChanged || returnsChanged;

  const reasonOf = (problem: UrlProblem): string => t(`${K}.${PROBLEM_KEYS[problem]}`);
  const parseReturn = (raw: string): ListEntryResult => {
    const result = parseHttpsUrl(raw);
    return result.ok ? result : { ok: false, error: reasonOf(result.problem) };
  };
  const labels: ListEditorLabels = {
    add: t(`${K}.add`),
    placeholder: t(`${K}.placeholder`),
    empty: t(`${K}.empty`),
    count: (count, max) => t(`${K}.count`, { count, max }),
    remove: (address) => t(`${K}.remove`, { address }),
    duplicate: (address) => t(`${K}.duplicate`, { address }),
    full: t(`${K}.full`, { max: LAUNCH_RETURN_URLS_MAX }),
    added: (address) => t(`${K}.added`, { address }),
    removed: (address) => t(`${K}.removed`, { address }),
  };
  const reset = () => {
    setMintDraft(null);
    setReturnDraft(null);
    setMintError(null);
    saver.clearFailure();
  };
  /** Judge the typed mint address now (on leaving the field or Enter), so the field itself carries the error. */
  const checkMint = (typed: string): string | null | undefined => {
    const trimmed = typed.trim();
    if (trimmed === "") {
      setMintError(null);
      return null;
    }
    const parsed = parseHttpsUrl(trimmed);
    if (!parsed.ok) {
      setMintError(reasonOf(parsed.problem));
      return undefined;
    }
    setMintError(null);
    return parsed.value;
  };
  const save = async () => {
    let mintValue: string | null | undefined;
    if (mintChanged) {
      mintValue = checkMint(mintDraft);
      if (mintValue === undefined) return;
    }
    const saved = await saver.save({ ...(mintValue !== undefined ? { mint_url: mintValue } : {}), ...(returnsChanged ? { return_urls: returns } : {}) });
    if (saved) reset();
  };
  return (
    <section className="card" aria-labelledby="urls-title" data-testid="urls-card">
      <div className="card-header flex items-start justify-between gap-3">
        <div className="min-w-0">
          <h2 id="urls-title" className="card-title">
            {t(`${K}.title`)}
          </h2>
          <p className="mt-0.5 text-xs text-fg-muted">{t(`${K}.subtitle`)}</p>
        </div>
        <Badge tone={settings.mint_url ? "success" : "neutral"} dot data-testid="urls-status">
          {t(settings.mint_url ? `${K}.statusSet` : `${K}.statusNotSet`)}
        </Badge>
      </div>
      <div className="space-y-5 px-5 py-4">
        <Field label={t(`${K}.mintLabel`)} hint={t(`${K}.mintHint`)} error={mintError ?? undefined}>
          <Input
            type="url"
            inputMode="url"
            autoComplete="off"
            spellCheck={false}
            className="font-mono"
            placeholder={t(`${K}.mintPlaceholder`)}
            value={mint}
            disabled={!manage.allowed}
            onChange={(event) => { setMintDraft(event.target.value); setMintError(null); }}
            onBlur={() => { if (mintChanged) checkMint(mintDraft); }}
            onKeyDown={(event) => {
              if (event.key !== "Enter" || event.nativeEvent.isComposing) return;
              event.preventDefault();
              if (mintChanged) checkMint(mintDraft);
            }}
            data-testid="mint-url"
          />
        </Field>
        <ListEditor
          label={t(`${K}.returnLabel`)}
          hint={t(`${K}.returnHint`)}
          items={returns}
          onChange={(next) => { setReturnDraft(next); saver.clearFailure(); }}
          parse={parseReturn}
          max={LAUNCH_RETURN_URLS_MAX}
          labels={labels}
          icon="link"
          disabled={!manage.allowed}
        />
        {manage.allowed ? null : <p className="text-xs text-fg-muted" data-testid="urls-read-only">{manage.sessionReadOnly ? manage.reason : t(`${K}.readOnly`)}</p>}
        {saver.failure ? (
          <p role="alert" className="text-xs font-semibold text-danger" data-testid="urls-error">
            {saver.failure}
          </p>
        ) : null}
      </div>
      <div className="flex flex-wrap items-center justify-end gap-2 border-t border-line px-5 py-3">
        <Button variant="secondary" disabled={!dirty || saver.busy} onClick={reset}>
          {t(`${K}.discard`)}
        </Button>
        <Gate reason={manage.reason}>
          <Button loading={saver.busy} disabled={!manage.allowed || !dirty} onClick={() => void save()}>
            {saver.busy ? t(`${K}.saving`) : t(`${K}.save`)}
          </Button>
        </Gate>
      </div>
    </section>
  );
}
